import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DateField from "@/components/DateField";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Clock, Plus, Trash2, AlertCircle, ShieldCheck, PlayCircle, FileDown, BellRing, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const WEEKDAYS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

export default function BKRInstellingenPage() {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const canManage = ["superadmin", "platform_admin", "directeur", "beheerder", "locatiemanager"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rL, rG, rB] = await Promise.all([
        api.get("/locations"),
        api.get("/groups"),
        api.get("/bkr/afwijktijden"),
      ]);
      setLocations(rL.data);
      setGroups(rG.data);
      setRows(rB.data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (row) => {
    if (!window.confirm(`Afwijktijd ${row.start}–${row.end} verwijderen?`)) return;
    try {
      await api.delete(`/bkr/afwijktijden/${row.id}`);
      toast.success("Verwijderd");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const groupMap = Object.fromEntries(groups.map(g => [g.id, g]));
  const locMap = Object.fromEntries(locations.map(l => [l.id, l]));

  // Groep per weekdag: totaal min per (group, weekday)
  const perGroupDay = {};
  for (const r of rows) {
    const k = `${r.group_id}|${r.weekday}`;
    perGroupDay[k] = (perGroupDay[k] || 0) + (r.duur_min || 0);
  }

  return (
    <div>
      <PageHeader
        title="BKR-afwijktijden"
        subtitle="Geplande momenten waarop personeelsbezetting rechtsgeldig onder de BKR mag zakken (drie-uursregeling). Maximaal 180 minuten per dag per stamgroep."
        testid="bkr-instellingen-header"
        actions={
          canManage && (
            <Button onClick={() => { setEditing({}); setDialogOpen(true); }} data-testid="bkr-add-btn" style={{ background: "var(--sage)" }} className="text-white">
              <Plus className="h-4 w-4 mr-1" /> Nieuwe afwijktijd
            </Button>
          )
        }
      />

      <Card className="p-4 bg-white shadow-none mb-4">
        <div className="flex items-start gap-3 text-xs text-neutral-600">
          <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-neutral-800">Wettelijk kader (drie-uursregeling)</div>
            <div>KDV: max 3 uur/dag bij ≥10u aaneengesloten opvang. BSO schooldag max 30 min; BSO hele dag max 3 uur. Tijdens afwijking min helft van vereiste PM's aanwezig. Officiële berekening via <a className="underline" href="https://www.1ratio.nl" target="_blank" rel="noreferrer">1ratio.nl</a>.</div>
          </div>
        </div>
      </Card>

      {loading && <div className="text-sm text-neutral-500">Laden…</div>}

      {!loading && rows.length === 0 && (
        <Card className="p-12 bg-white shadow-none text-center">
          <Clock className="h-8 w-8 mx-auto text-neutral-300 mb-3" />
          <div className="text-sm text-neutral-600">Nog geen afwijktijden ingesteld.</div>
          {canManage && (
            <Button onClick={() => { setEditing({}); setDialogOpen(true); }} className="mt-3" style={{ background: "var(--sage)" }} data-testid="bkr-empty-add-btn">
              <Plus className="h-4 w-4 mr-1" /> Eerste toevoegen
            </Button>
          )}
        </Card>
      )}

      {!loading && rows.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Object.entries(rows.reduce((acc, r) => {
            (acc[r.group_id] ||= []).push(r); return acc;
          }, {})).map(([gid, list]) => {
            const g = groupMap[gid] || {};
            const l = locMap[g.location_id] || {};
            return (
              <Card key={gid} className="p-4 bg-white shadow-none" data-testid={`bkr-group-${gid}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-display text-base tracking-tight" style={{ color: g.color || "var(--sage)" }}>{g.name || "—"}</h3>
                    <div className="text-[11px] text-neutral-500">{l.name || "—"} · {l.type || "?"}</div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {list.map(r => {
                    const dayTotal = perGroupDay[`${r.group_id}|${r.weekday}`] || 0;
                    const tone = dayTotal > 180 ? "text-red-700" : dayTotal > 150 ? "text-amber-700" : "text-emerald-700";
                    return (
                      <div key={r.id} className="flex items-center justify-between p-2 rounded border border-neutral-200 text-sm">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="bg-neutral-50 text-neutral-700 border-neutral-200 min-w-[40px] justify-center">{WEEKDAYS[r.weekday]}</Badge>
                          <span className="font-mono">{r.start}–{r.end}</span>
                          <span className={`text-[11px] ${tone}`}>{r.duur_min}min · totaal {dayTotal}/180</span>
                          {r.label && <span className="text-[11px] text-neutral-500 italic">— {r.label}</span>}
                        </div>
                        {canManage && (
                          <Button variant="ghost" size="sm" onClick={() => del(r)} data-testid={`bkr-del-${r.id}`} className="text-red-600 hover:bg-red-50 h-7 px-2">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <AfwijktijdDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onDone={() => { setDialogOpen(false); load(); }}
        locations={locations}
        groups={groups}
        editing={editing}
      />

      {/* Simulator + GGD-export */}
      {canManage && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
          <SimulatorPanel />
          <GGDExportPanel />
        </div>
      )}

      {/* Historie-grafiek + Notif-test */}
      {canManage && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <HistoriePanel className="lg:col-span-2" />
          <NotifTestPanel />
        </div>
      )}
    </div>
  );
}


function NotifTestPanel() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/bkr/notif-test");
      setResult(r.data);
      const p = r.data.push?.sent || 0;
      const e = r.data.email?.sent;
      if (p > 0 && e) toast.success(`Test verstuurd — ${p} push + e-mail`);
      else if (p > 0) toast.success(`Push naar ${p} apparaat verstuurd (e-mail: ${r.data.email?.reason || '—'})`);
      else if (e) toast.success("E-mail verstuurd (geen push-apparaten geregistreerd)");
      else toast.warning("Geen push-apparaten én geen e-mail-integratie beschikbaar");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-4 bg-white shadow-none" data-testid="bkr-notif-test">
      <h3 className="font-display text-base tracking-tight flex items-center gap-2 mb-1">
        <BellRing className="h-4 w-4 text-[color:var(--terracotta)]" /> Notificatie-test
      </h3>
      <p className="text-[11px] text-neutral-500 mb-3">
        Stuur één dummy BKR-melding naar jouw eigen apparaten (push + e-mail) om te controleren dat de meldingen aankomen bij een echte BKR-overtreding.
      </p>
      <div className="text-[11px] text-neutral-600 mb-2">
        Ontvanger: <span className="font-mono">{user?.email || "—"}</span>
      </div>
      <Button onClick={run} disabled={busy} style={{ background: "var(--terracotta)" }} className="text-white w-full" data-testid="bkr-notif-test-btn">
        {busy ? "Versturen…" : "Verstuur test-melding"}
      </Button>
      {result && (
        <div className="mt-3 p-2 rounded border border-neutral-200 bg-neutral-50 text-[11px] space-y-1" data-testid="bkr-notif-test-result">
          <div>Push: <strong>{result.push?.sent || 0}</strong> apparaat(en)  {result.push?.failed ? `· ${result.push.failed} mislukt` : ""}</div>
          <div>E-mail: <strong>{result.email?.sent ? "verstuurd" : "niet verzonden"}</strong>{result.email?.reason && <span className="text-neutral-500"> · {result.email.reason.slice(0, 60)}</span>}</div>
          {(!result.push?.sent && !result.email?.sent) && (
            <div className="text-amber-700 pt-1 border-t border-neutral-200">
              💡 Geen push? Activeer meldingen op je apparaat via <em>Instellingen → Meldingen</em>.
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


function HistoriePanel({ className = "" }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => {
    api.get(`/bkr/afwijk-log/historie?days=${days}`)
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, [days]);

  const series = data?.series || [];

  // SVG mini-line chart per groep
  const renderSpark = (points, maxY = 180) => {
    if (!points || points.length === 0) return null;
    const W = 220, H = 44, PAD = 2;
    const N = points.length;
    const maxVal = Math.max(maxY, ...points.map(p => p.min));
    const yTop = PAD, yBot = H - PAD;
    const xStep = (W - PAD * 2) / Math.max(1, N - 1);
    const path = points.map((p, i) => {
      const x = PAD + i * xStep;
      const y = yBot - (p.min / maxVal) * (yBot - yTop);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(" ");
    const y180 = yBot - (180 / maxVal) * (yBot - yTop);
    return (
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block">
        <line x1={PAD} y1={y180} x2={W - PAD} y2={y180} stroke="#f59e0b" strokeDasharray="2,2" strokeWidth="0.8" />
        <path d={path} fill="none" stroke="var(--sage)" strokeWidth="1.5" />
        {points.map((p, i) => {
          if (p.min === 0) return null;
          const x = PAD + i * xStep;
          const y = yBot - (p.min / maxVal) * (yBot - yTop);
          return <circle key={i} cx={x} cy={y} r={1.6} fill={p.min > 180 ? "#dc2626" : p.min >= 150 ? "#f59e0b" : "var(--sage)"} />;
        })}
      </svg>
    );
  };

  return (
    <Card className={`p-4 bg-white shadow-none ${className}`} data-testid="bkr-historie-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-base tracking-tight flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[color:var(--sage)]" /> Historie · laatste {days} dagen
        </h3>
        <div className="flex gap-1">
          {[7, 30, 90].map(n => (
            <button key={n} onClick={() => setDays(n)}
              data-testid={`historie-days-${n}`}
              className={`px-2 py-1 text-[10px] rounded ${days === n ? "bg-[color:var(--sage)] text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}>
              {n}d
            </button>
          ))}
        </div>
      </div>
      {series.length === 0 ? (
        <div className="text-sm text-neutral-500">Geen afwijkingen geregistreerd in deze periode.</div>
      ) : (
        <div className="space-y-2">
          {series.map(s => (
            <div key={s.group_id} className="flex items-center gap-3 border-t border-neutral-100 pt-2 first:border-0 first:pt-0" data-testid={`historie-group-${s.group_id}`}>
              <div className="w-32 shrink-0">
                <div className="text-sm font-medium truncate">{s.group_name}</div>
                <div className="text-[10px] text-neutral-500">
                  {s.dagen_met_afwijking}d met afwijking · <span className={s.dagen_over_180 > 0 ? "text-red-700 font-semibold" : ""}>{s.dagen_over_180}d &gt;180 min</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                {renderSpark(s.points, data.wettelijk_max_min)}
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-mono">{s.totaal_min} min</div>
                <div className="text-[10px] text-neutral-500">totaal · piek {s.max_min_op_1_dag}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


function SimulatorPanel() {
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reden, setReden] = useState("");
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/bkr/simulate", { datum });
      setResult(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const tone = (s) => ({
    ok: "bg-emerald-50 text-emerald-700",
    afwijk_gepland: "bg-amber-50 text-amber-700",
    afwijk_ongepland: "bg-orange-50 text-orange-700",
    overtreding: "bg-red-100 text-red-800",
  }[s] || "bg-neutral-50 text-neutral-600");
  const anyViolation = result?.groepen?.some(g => g.verwachte_overtredingen_slots > 0);

  return (
    <Card className="p-4 bg-white shadow-none" data-testid="bkr-simulator">
      <h3 className="font-display text-base tracking-tight flex items-center gap-2 mb-1">
        <PlayCircle className="h-4 w-4 text-[color:var(--sage)]" /> Planning-simulator
      </h3>
      <p className="text-[11px] text-neutral-500 mb-3">
        Simuleer of de dag met contract-planning + rooster-shifts + ingestelde afwijktijden BKR-proof is. Toont per 30-min slot.
      </p>
      <div className="flex items-end gap-2 mb-3">
        <div className="flex-1">
          <Label className="text-xs">Datum</Label>
          <DateField value={datum} onChange={setDatum} data-testid="sim-datum" />
        </div>
        <Button onClick={run} disabled={busy} style={{ background: "var(--sage)" }} className="text-white" data-testid="sim-run">
          {busy ? "Rekenen…" : "Simuleer"}
        </Button>
      </div>
      {result && (
        <div className="space-y-3">
          {anyViolation && (
            <div className="p-2 rounded border border-red-300 bg-red-50 text-xs text-red-800" data-testid="sim-violation-banner">
              <div className="font-semibold">Verwachte overtredingen — publicatie niet aanbevolen</div>
              <Input value={reden} onChange={e => setReden(e.target.value)} placeholder="Reden + corrigerende actie…" className="mt-2" data-testid="sim-reden" />
            </div>
          )}
          {result.groepen.map(g => (
            <div key={g.group_id} className="border border-neutral-200 rounded p-2" data-testid={`sim-group-${g.group_id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-medium text-sm">{g.group_name}</div>
                <div className="text-[10px] text-neutral-500">
                  {g.kinderen_ingepland} kind · {g.totaal_geplande_afwijking_min}/180 afwijk min · <strong className={g.verwachte_overtredingen_slots > 0 ? "text-red-700" : "text-emerald-700"}>{g.verwachte_overtredingen_slots} slot-overtredingen</strong>
                </div>
              </div>
              <div className="flex flex-wrap gap-0.5">
                {g.slots.map(s => (
                  <div key={s.tijd} className={`px-1.5 py-0.5 text-[9px] rounded ${tone(s.status)}`} title={`${s.tijd}: ${s.gepland_pm}/${s.vereist} pm${s.in_venster ? " · in venster" : ""}`}>
                    {s.tijd}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


function GGDExportPanel() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(); monthAgo.setDate(monthAgo.getDate() - 30);
  const [from, setFrom] = useState(monthAgo.toISOString().slice(0, 10));
  const [to, setTo] = useState(today);
  const [busy, setBusy] = useState(false);

  const download = async (fmt) => {
    setBusy(true);
    try {
      const r = await api.get("/bkr/afwijk-log/export", {
        params: { from_date: from, to_date: to, fmt },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: fmt === "pdf" ? "application/pdf" : "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bkr-afwijkingen-${from}_${to}.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} klaar`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-4 bg-white shadow-none" data-testid="bkr-ggd-export">
      <h3 className="font-display text-base tracking-tight flex items-center gap-2 mb-1">
        <FileDown className="h-4 w-4 text-[color:var(--sage)]" /> GGD-auditlog export
      </h3>
      <p className="text-[11px] text-neutral-500 mb-3">
        Alle werkelijke afwijkingsperioden in gekozen periode — voor GGD-inspectie. Onveranderbaar audit-log met start, eind, duur, kinderen, PM's en rood-status.
      </p>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <Label className="text-xs">Van datum</Label>
          <DateField value={from} onChange={setFrom} max={to} data-testid="ggd-from" />
        </div>
        <div>
          <Label className="text-xs">Tot en met</Label>
          <DateField value={to} onChange={setTo} min={from} data-testid="ggd-to" />
        </div>
      </div>
      <div className="flex gap-2">
        <Button onClick={() => download("csv")} disabled={busy} variant="outline" data-testid="ggd-download-csv">
          <FileDown className="h-3.5 w-3.5 mr-1" /> CSV
        </Button>
        <Button onClick={() => download("pdf")} disabled={busy} style={{ background: "var(--sage)" }} className="text-white" data-testid="ggd-download-pdf">
          <FileDown className="h-3.5 w-3.5 mr-1" /> PDF
        </Button>
      </div>
    </Card>
  );
}


function AfwijktijdDialog({ open, onClose, onDone, locations, groups, editing }) {
  const [form, setForm] = useState({ location_id: "", group_id: "", weekday: "0", start: "", end: "", label: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ location_id: "", group_id: "", weekday: "0", start: "", end: "", label: "" });
  }, [open]);

  const groupOptions = form.location_id
    ? groups.filter(g => g.location_id === form.location_id)
    : groups;

  const submit = async () => {
    if (!form.location_id || !form.group_id) return toast.error("Locatie en groep verplicht");
    if (!form.start || !form.end) return toast.error("Start- en eindtijd verplicht");
    setBusy(true);
    try {
      await api.put("/bkr/afwijktijden", { ...form, weekday: Number(form.weekday) });
      toast.success("Afwijktijd opgeslagen");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="bkr-dialog">
        <DialogHeader>
          <DialogTitle>Afwijktijd toevoegen</DialogTitle>
          <DialogDescription>
            Geef aan wanneer volgens de personeelsplanning van de BKR mag worden afgeweken (drie-uursregeling).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Locatie</Label>
            <select value={form.location_id} onChange={e => setForm({ ...form, location_id: e.target.value, group_id: "" })}
              className="w-full h-9 rounded-md border border-neutral-200 px-2 text-sm mt-1" data-testid="bkr-location">
              <option value="">— kies locatie —</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name} ({l.type})</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Stamgroep</Label>
            <select value={form.group_id} onChange={e => setForm({ ...form, group_id: e.target.value })}
              className="w-full h-9 rounded-md border border-neutral-200 px-2 text-sm mt-1" data-testid="bkr-group">
              <option value="">— kies groep —</option>
              {groupOptions.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Weekdag</Label>
            <select value={form.weekday} onChange={e => setForm({ ...form, weekday: e.target.value })}
              className="w-full h-9 rounded-md border border-neutral-200 px-2 text-sm mt-1" data-testid="bkr-weekday">
              {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d} ({["Maandag","Dinsdag","Woensdag","Donderdag","Vrijdag","Zaterdag","Zondag"][i]})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Starttijd</Label>
              <Input type="time" value={form.start} onChange={e => setForm({ ...form, start: e.target.value })} data-testid="bkr-start" />
            </div>
            <div>
              <Label className="text-xs">Eindtijd</Label>
              <Input type="time" value={form.end} onChange={e => setForm({ ...form, end: e.target.value })} data-testid="bkr-end" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Label (optioneel)</Label>
            <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="bv. Ochtendpauze" data-testid="bkr-label" />
          </div>
          <div className="text-[11px] text-neutral-500 pt-1 border-t border-neutral-100">
            De validator controleert automatisch: HH:MM formaat, start&lt;eind, geen overlap met bestaande vensters, som ≤180 min/dag/groep.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button onClick={submit} disabled={busy} style={{ background: "var(--sage)" }} className="text-white" data-testid="bkr-submit">
            {busy ? "Opslaan…" : "Opslaan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
